// Das Protokoll eines Anrufs als schwebendes Fenster statt als Modal: waehrend
// des Gespraechs bleibt die ganze Seite bedienbar — im Auftrag nachsehen,
// solange der Kunde noch dran ist, war der Grund.
//
// Gemountet wird es EINMAL in Routes.js, ausserhalb des <Switch> — nur deshalb
// ueberlebt es einen Seitenwechsel. Der Store dahinter ist derselbe Modul-Store
// wie in ActivitySpeedDial (useSyncExternalStore); ein Redux-Slice fuer einen
// einzigen Wert waere Kabelage ohne Nutzen.
//
// ponytail: nicht verschiebbar. Fest unten rechts, einklappbar — das Navigieren
// war der Wunsch, das Ziehen die Verpackung. Nachruesten (mousedown/mousemove
// auf den Kopf), wenn es tatsaechlich einmal etwas verdeckt.
import React, { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { Button, Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import CallOutcomePanel from "./CallOutcomePanel";
import { formatPhoneNumber } from "../../helpers/telephony";
import HiddenValue from "../HiddenValue";

let current = null;
const listeners = new Set();
const emit = () => listeners.forEach((listener) => listener());
const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/**
 * Oeffnet das Protokollfenster. Alles, was `CallOutcomePanel` kennt, geht durch:
 * company, contacts, companyID, contactPersonId, dialResult, direction, task,
 * objectId, objectType, unknownNumber, onSaved. Dazu `subject`: wen man anruft,
 * wenn kein Firmendatensatz mitkommt — nur fuer den Kopf.
 *
 * Ein zweiter Aufruf ersetzt den ersten — zwei Anrufe gleichzeitig gibt es nicht,
 * und `key` sorgt dafuer, dass dabei kein Rest des vorigen stehen bleibt.
 */
export const openCallPanel = (props) => {
  current = { ...props, key: String(Date.now()) };
  emit();
};

export const closeCallPanel = () => {
  current = null;
  emit();
};

const CallPanelBody = ({ call }) => {
  const { t } = useTranslation(["components", "common"]);
  const { user } = useSelector((state) => ({ user: state.Auth.user }));
  const [saving, setSaving] = useState(false);
  const [outcome, setOutcome] = useState("");
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const panel = useRef(null);

  const inbound = call.direction === "inbound";

  const requestClose = () => {
    if (saving) return;
    if (!confirmDiscard && panel.current?.hasInput?.()) {
      setConfirmDiscard(true);
      setCollapsed(false);
      return;
    }
    closeCallPanel();
  };

  return (
    <Card
      className="call-panel shadow-lg mb-0"
      style={{
        position: "fixed",
        right: "1rem",
        bottom: "1rem",
        width: "440px",
        maxWidth: "calc(100vw - 2rem)",
        /* UEBER jedem Modal (_modal.scss deckelt die bei 1100). Ein laufendes
           Gespraech ist das Dringendste auf dem Schirm: haengt das Fenster hinter
           einem Modal-Backdrop, ist es unsichtbar und nicht bedienbar — und der
           Anruf wird nicht festgehalten. Verdeckt es etwas, klappt der Pfeil im
           Kopf es weg. */
        zIndex: 1110,
      }}
    >
      <Card.Header className="d-flex align-items-center gap-2 py-2 px-3">
        <i
          className={
            "mdi font-18 text-success " +
            (inbound ? "mdi-phone-incoming" : "mdi-phone-outgoing")
          }
        />
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          <div className="fw-bold text-truncate">
            {inbound ? t("Log incoming call") : t("Log the call")}
          </div>
          <div className="text-muted font-12 text-truncate">
            {/* The company name is a `partners` value; the phone number beside it is not. */}
            <HiddenValue dataClass="partners">
              {call.company?.name || call.subject}
            </HiddenValue>
            {formatPhoneNumber(call.dialResult?.number)
              ? ` · ${formatPhoneNumber(call.dialResult?.number)}`
              : ""}
          </div>
        </div>
        <Button
          variant="light"
          size="sm"
          className="py-0 px-1 lh-1"
          title={collapsed ? t("Expand") : t("Collapse")}
          onClick={() => setCollapsed((prev) => !prev)}
        >
          <i
            className={"mdi font-18 " + (collapsed ? "mdi-chevron-up" : "mdi-chevron-down")}
          />
        </Button>
        <Button
          variant="light"
          size="sm"
          className="py-0 px-1 lh-1"
          title={t("Close")}
          disabled={saving}
          onClick={requestClose}
        >
          <i className="mdi mdi-close font-18" />
        </Button>
      </Card.Header>

      {/* Eingeklappt bleibt der Kopf stehen — der Anruf laeuft weiter mit, das
          Fenster nimmt aber keinen Platz mehr weg. Der Inhalt wird NICHT
          ausgehaengt, sonst waeren Notiz und Zeit weg. */}
      <div hidden={collapsed}>
        {/* Gescrollt wird nur, wenn der Bildschirm nicht reicht: die Grenze ist die
            Fensterhoehe minus Kopf, Fuss und dem Rand unten — keine feste Zahl. */}
        <Card.Body
          className="p-3"
          style={{ maxHeight: "calc(100vh - 11rem)", overflowY: "auto" }}
        >
          <CallOutcomePanel
            ref={panel}
            dialResult={call.dialResult}
            direction={call.direction}
            companyID={call.companyID || call.company?.id}
            contactPersonId={call.contactPersonId}
            unknownNumber={call.unknownNumber}
            company={call.company}
            contacts={call.contacts}
            objectId={call.objectId}
            objectType={call.objectType}
            task={call.task}
            userId={user?.id}
            onBusy={setSaving}
            onOutcomeChange={setOutcome}
            onSaved={(activity) => {
              closeCallPanel();
              if (typeof call.onSaved === "function") call.onSaved(activity);
            }}
          />
        </Card.Body>

        <Card.Footer
          className={
            "py-2 px-3 " + (confirmDiscard ? "d-flex justify-content-between align-items-center gap-2" : "text-end")
          }
        >
          {confirmDiscard ? (
            <>
              <span className="text-muted font-12">
                {t("Discard this call without logging it?")}
              </span>
              <span className="text-nowrap">
                <Button
                  variant="light"
                  size="sm"
                  className="me-1"
                  onClick={() => setConfirmDiscard(false)}
                >
                  {t("Cancel")}
                </Button>
                <Button variant="primary" size="sm" onClick={closeCallPanel}>
                  {t("Discard")}
                </Button>
              </span>
            </>
          ) : (
            <Button
              variant="primary"
              size="sm"
              disabled={!outcome || saving}
              onClick={() => panel.current?.save()}
            >
              {saving ? t("Saving...") : t("Save")}
            </Button>
          )}
        </Card.Footer>
      </div>
    </Card>
  );
};

const CallPanel = () => {
  const call = useSyncExternalStore(subscribe, () => current);

  // Der Plus-Knopf und "nach oben" stehen an derselben Ecke — solange das Fenster
  // offen ist, ruecken sie daneben (siehe _modal.scss).
  useEffect(() => {
    document.body.classList.toggle("call-panel-open", Boolean(call));
    return () => document.body.classList.remove("call-panel-open");
  }, [call]);

  if (!call) return null;
  // Neuer Anruf = frischer Zustand, samt Zaehler und Notiz.
  return <CallPanelBody key={call.key} call={call} />;
};

export default CallPanel;
